/* eslint-disable react-hooks/exhaustive-deps */
import { 
  Button, Dialog, DialogActions, DialogContent, Grid, Paper, Typography, 
  IconButton, Tooltip, TextField, InputAdornment, 
  DialogTitle
} from "@mui/material";
import { useEffect, useState } from "react";
import MyTable, { TableRow } from "../../../components/MyTable";
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { TableHeadRows } from "../../../components/MyTable/TableHead";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import MySnackbar from "../../../components/MySnackbar";
import { getDomainMappingsByRuc, getRejectionTrackers, resetRejectionTracker } from "../../../api/emailProcess";
import DomainMappingManager from "../../../components/DomainMappingManager";
import BlockEmailButton from "../../../components/BlockEmailButton";

const sortTrackers = (trackers: any[]) => {
    return [...trackers].sort((a, b) => {
        if (a.removedCount !== b.removedCount) {
            return (b.removedCount || 0) - (a.removedCount || 0);
        }
        return (b.bounceCount || 0) - (a.bounceCount || 0);
    });
};

const EmailRejectionTrackerView = () => {
    const { connectionId, sessionData } = useSelector((state: RootState) => state.auth);
    const [trackers, setTrackers] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [snackBarMessage, setSnackBarMessage] = useState<any | undefined>();
    const [resetConfirmOpen, setResetConfirmOpen] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<any | null>(null);
    const [resetting, setResetting] = useState<boolean>(false);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [searchEmail, setSearchEmail] = useState<string>('');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [allowedDomains, setAllowedDomains] = useState<string[]>([]);

    const tableHeaders: TableHeadRows[] = [
        { label: "Email", rowKeyName: "email" },
        { label: "RUC", rowKeyName: "ruc" },
        { label: "Bounces", rowKeyName: "bounceCount" },
        { label: "Desbloqueos automáticos", rowKeyName: "removedCount", },
        { label: "Desbloqueos manuales", rowKeyName: "manualResetCount" },
        { label: "Última actualización", rowKeyName: "lastUpdated" },
        {
            label: "Reiniciar contador",
            rowKeyName: "reset",
            customValue: (headRow: any, row: TableRow<string, any>) => {
                if ((row.removedCount || 0) > 3) {
                    return (
                        <Tooltip title="Reiniciar contador (eliminar de la lista de supresión SES)">
                            <IconButton 
                                color="error" 
                                onClick={() => handleReset(row)}
                                disabled={resetting}
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    );
                }
                return <></>;
            },
        },
    ];

    const fetchRejectionTrackers = async (reset: boolean = true, token?: string, email?: string) => {
        if (reset) {
            setLoading(true);
            setTrackers([]);
            setNextToken(null);
        } else {
            setLoadingMore(true);
        }
        try {
            const response = await getRejectionTrackers(token, email);
            if (response && response.items) {
                let newItems = response.items;
                if (reset) {
                    newItems = sortTrackers(newItems);
                    setTrackers(newItems);
                } else {
                    setTrackers(prev => sortTrackers([...prev, ...newItems]));
                }
                setNextToken(response.nextToken || null);
            } else {
                setTrackers([]);
                console.error("Unexpected response format:", response);
                setSnackBarMessage({ success: false, msg: response.message || "Error en el formato de respuesta" });
            }
        } catch (error: any) {
            setTrackers([])
            console.error("Error fetching rejection trackers:", error);
            setSnackBarMessage({ success: false, msg: error.message || "Error al cargar los correos bloqueados" });
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchUserAllowedDomains = async () => {
        if (!sessionData?.UserName) return;
        const domain = sessionData?.UserName.split('@')[1].toLowerCase();
        if (domain === 's10peru.com') {
            setAllowedDomains(['*']);
            return;
        }
        try {
            const {items} = await getDomainMappingsByRuc();
            console.log('allMappings', items)
            const domainsList = items.map((item: any) => item.domain);
            console.log('domainsList', domainsList)
            setAllowedDomains(domainsList);
        } catch (err) {
            console.error('Error obteniendo dominios permitidos', err);
            setAllowedDomains([]);
        }
    };
    const handleSearch = () => {
        if (!searchEmail.trim()) {
            clearSearch();
            return;
        }
        setIsSearching(true);
        fetchRejectionTrackers(true, undefined, searchEmail.trim());
    };

    const clearSearch = () => {
        setSearchEmail('');
        setIsSearching(false);
        fetchRejectionTrackers(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleReset = async (item: any) => {
        setSelectedItem(item);
        setResetConfirmOpen(true);
    };

    const confirmReset = async () => {
        if (!selectedItem) return;
        setResetting(true);
        setResetConfirmOpen(false);
        
        const result = await resetRejectionTracker(selectedItem.email);
        const success = result && result.message && !result.message.includes("Error");
        
        if (success) {
            setTrackers(prevTrackers => {
                const updatedTrackers = prevTrackers.map(item => {
                    if (item.email === selectedItem.email) {
                        return {
                            ...item,
                            removedCount: 0,
                            manualResetCount: (result.newManualResetCount !== undefined) 
                                ? result.newManualResetCount 
                                : (item.manualResetCount || 0) + 1,
                            lastUpdated: result.updatedAttributes?.lastUpdated || new Date().toISOString()
                        };
                    }
                    return item;
                });
                return sortTrackers(updatedTrackers);
            });
            
            setSnackBarMessage({ 
                success: true, 
                msg: result.message || "Contador reiniciado exitosamente"
            });
        } else {
            setSnackBarMessage({ 
                success: false, 
                msg: result?.message || "Error al reiniciar el contador"
            });
        }
        
        setResetting(false);
    };

    const cancelReset = () => {
        setResetConfirmOpen(false);
        setSelectedItem(null);
    };
    

    useEffect(() => {
        if (connectionId) {
            fetchRejectionTrackers(true);
            fetchUserAllowedDomains()
        }
    }, [connectionId]);

    return (
        <Grid item width="100%" marginTop="1.5rem" minHeight="60%">
            <Paper 
                elevation={3}
                style={{
                    width: "100%", 
                    borderRadius: "0.5rem",
                    minHeight: "10rem",
                    padding: "2rem 1.5rem"
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <div>
                        {allowedDomains.length > 0 && <BlockEmailButton allowedDomains={allowedDomains} />}
                    </div>
                    <div>
                        <DomainMappingManager />
                    </div>
                    <TextField
                        size="small"
                        placeholder="Buscar por email"
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: isSearching && searchEmail && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={clearSearch}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>
                <MyTable
                    tableHeadRows={tableHeaders}
                    tableRows={trackers}
                    loading={loading}
                    hiddenSearchInput={true}
                    tableOptionsBar={{
                        useDrawer: false,
                    }}
                    // onLoadMore={loadMore} 
                    // hasMore={!!nextToken && !isSearching}
                    // loadingMore={loadingMore}
                />
            </Paper>

            <Dialog open={resetConfirmOpen} onClose={cancelReset}>
                <DialogTitle>Confirmar reinicio de contador</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas reiniciar el contador y desbloquear el correo {selectedItem?.email || 'desconocido'}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelReset} disabled={resetting}>Cancelar</Button>
                    <Button onClick={confirmReset} color="error" disabled={resetting} autoFocus>
                        {resetting ? "Reiniciando..." : "Confirmar reinicio"}
                    </Button>
                </DialogActions>
            </Dialog>

            {snackBarMessage && <MySnackbar 
                msg={snackBarMessage.msg} 
                severity={snackBarMessage.success ? 'success' : 'error'}
                onClose={() => setSnackBarMessage(undefined)}
                open={true}
            />}
        </Grid>
    );
};

export default EmailRejectionTrackerView;